import type { CanonicalWork } from "./generated/contracts";

export type ArtworkKind = "portrait" | "identity-art";
export type ContributorArtwork = { image: string; kind: ArtworkKind };

export const contributorArtwork: Record<string, ContributorArtwork> = {
  "walter-cummins": {
    image: "/art/teachers/walter-cummins.webp",
    kind: "portrait",
  },
  "victor-paul-wierwille": {
    image: "/art/teachers/victor-paul-wierwille.webp",
    kind: "portrait",
  },
  "ew-bullinger": {
    image: "/art/teachers/ew-bullinger.webp",
    kind: "portrait",
  },
  "kc-pillai": { image: "/art/teachers/kc-pillai.webp", kind: "portrait" },
  "george-lamsa": {
    image: "/art/teachers/george-lamsa.webp",
    kind: "portrait",
  },
  "craig-martindale": {
    image: "/art/teachers/craig-martindale.webp",
    kind: "portrait",
  },
  "earl-burton": { image: "/art/teachers/earl-burton.webp", kind: "portrait" },
  "randy-anderson": {
    image: "/art/teachers/randy-anderson.webp",
    kind: "portrait",
  },
  "bo-reahard": { image: "/art/teachers/bo-reahard.webp", kind: "portrait" },
  "donna-martindale": {
    image: "/art/teachers/donna-martindale.webp",
    kind: "portrait",
  },
  townsend: { image: "/art/teachers/townsend.webp", kind: "portrait" },
  "vince-finnegan": {
    image: "/art/teachers/vince-finnegan.webp",
    kind: "portrait",
  },
  "wayne-clapp": { image: "/art/teachers/wayne-clapp.webp", kind: "portrait" },
  "john-crouch": { image: "/art/teachers/john-crouch.webp", kind: "portrait" },
  "david-finnegan": {
    image: "/art/teachers/david-finnegan.webp",
    kind: "identity-art",
  },
  dubofsky: { image: "/art/teachers/dubofsky.webp", kind: "identity-art" },
  "harry-wierwille": {
    image: "/art/teachers/harry-wierwille.webp",
    kind: "identity-art",
  },
  "francis-winkowitsch": {
    image: "/art/teachers/francis-winkowitsch.webp",
    kind: "identity-art",
  },
  "dorothy-owens": {
    image: "/art/teachers/dorothy-owens.webp",
    kind: "identity-art",
  },
  "a-brown": { image: "/art/teachers/a-brown.webp", kind: "identity-art" },
  editorial: { image: "/art/teachers/editorial.webp", kind: "identity-art" },
};

// Compatibility export for existing UI; this now intentionally includes only sourced portraits.
export const teacherPortraits = Object.fromEntries(
  Object.entries(contributorArtwork)
    .filter(([, artwork]) => artwork.kind === "portrait")
    .map(([slug, artwork]) => [slug, artwork.image]),
);

export const featuredTeachers = [
  {
    slug: "walter-cummins",
    name: "Walter Cummins",
    note: "Teachings and Scripture studies",
  },
  {
    slug: "victor-paul-wierwille",
    name: "Victor Paul Wierwille",
    note: "Foundational teachings",
  },
  {
    slug: "kc-pillai",
    name: "Bishop K. C. Pillai",
    note: "Eastern customs and Scriptures",
  },
  {
    slug: "george-lamsa",
    name: "George M. Lamsa",
    note: "Aramaic and Bible studies",
  },
  {
    slug: "craig-martindale",
    name: "Craig Martindale",
    note: "Bible teachings",
  },
  {
    slug: "earl-burton",
    name: "Earl Burton",
    note: "Teachings in the archive",
  },
  { slug: "vince-finnegan", name: "Vince Finnegan", note: "Bible teachings" },
  {
    slug: "ew-bullinger",
    name: "E. W. Bullinger",
    note: "Books and reference works",
  },
];

type TopicArtwork = { words: string[]; image: string };
export const topicArtwork: TopicArtwork[] = [
  {
    words: [
      "renewed mind",
      "renewing the mind",
      "wisdom",
      "understanding",
      "knowledge",
      "study",
      "scripture",
    ],
    image: "/art/topics/renewed-mind.webp",
  },
  {
    words: ["holy spirit", "pneuma", "speaking in tongues"],
    image: "/art/topics/holy-spirit.webp",
  },
  {
    words: ["healing", "health", "wholeness", "made whole"],
    image: "/art/topics/healing.webp",
  },
  {
    words: ["love", "fellowship", "church", "body of christ", "one another"],
    image: "/art/topics/love-fellowship.webp",
  },
  {
    words: ["hope", "comfort", "return", "future"],
    image: "/art/topics/hope.webp",
  },
  {
    words: ["believing", "faith", "trust", "promise", "receive"],
    image: "/art/topics/believing.webp",
  },
  { words: ["grace", "favor"], image: "/art/topics/grace.webp" },
  { words: ["redemption", "redeemed"], image: "/art/topics/redemption.webp" },
  {
    words: ["righteousness", "justified", "justification"],
    image: "/art/topics/righteousness.webp",
  },
  {
    words: ["sonship", "sons of god", "children of god"],
    image: "/art/topics/sonship.webp",
  },
  {
    words: ["prayer", "pray", "intercession"],
    image: "/art/topics/prayer.webp",
  },
  { words: ["worship", "praise"], image: "/art/topics/worship.webp" },
  {
    words: ["jesus christ", "christ jesus", "our lord"],
    image: "/art/topics/jesus-christ.webp",
  },
  {
    words: ["integrity", "accuracy", "word of god", "rightly dividing"],
    image: "/art/topics/integrity-of-the-word.webp",
  },
  {
    words: ["resurrection", "raised", "rising"],
    image: "/art/topics/resurrection.webp",
  },
  {
    words: ["forgiveness", "forgive", "forgiven"],
    image: "/art/topics/forgiveness.webp",
  },
  { words: ["peace", "rest", "anxiety"], image: "/art/topics/peace-rest.webp" },
  { words: ["joy", "rejoice"], image: "/art/topics/joy.webp" },
  { words: ["courage", "boldness", "fear"], image: "/art/topics/courage.webp" },
  { words: ["discernment", "counsel"], image: "/art/topics/wisdom.webp" },
  {
    words: ["stewardship", "prosperity", "giving", "abundance"],
    image: "/art/topics/stewardship.webp",
  },
  {
    words: ["service", "ministry", "serve"],
    image: "/art/topics/service.webp",
  },
  {
    words: ["outreach", "evangelism", "witness"],
    image: "/art/topics/outreach.webp",
  },
  {
    words: ["family", "household", "marriage"],
    image: "/art/topics/family-household.webp",
  },
  {
    words: ["warfare", "stand firm", "standing"],
    image: "/art/topics/spiritual-warfare.webp",
  },
  {
    words: ["manifestation", "gifts of the spirit", "spiritual gifts"],
    image: "/art/topics/gifts-manifestations.webp",
  },
  { words: ["unity", "one body"], image: "/art/topics/unity-body.webp" },
  {
    words: ["gratitude", "thanksgiving", "thankful"],
    image: "/art/topics/gratitude.webp",
  },
  { words: ["abundant life", "abundant"], image: "/art/topics/abundance.webp" },
  {
    words: ["covenant", "faithfulness", "faithful"],
    image: "/art/topics/covenant-faithfulness.webp",
  },
];

const bibleBookArtwork: Record<string, string> = {
  acts: "/art/books/acts.webp",
  romans: "/art/books/romans.webp",
  corinthians: "/art/books/corinthians.webp",
  galatians: "/art/books/galatians.webp",
  ephesians: "/art/books/ephesians.webp",
  philippians: "/art/books/philippians.webp",
  colossians: "/art/books/colossians.webp",
  thessalonians: "/art/books/thessalonians.webp",
  timothy: "/art/books/timothy.webp",
  hebrews: "/art/books/hebrews.webp",
  psalms: "/art/books/psalms.webp",
  matthew: "/art/books/gospels.webp",
  mark: "/art/books/gospels.webp",
  luke: "/art/books/gospels.webp",
  john: "/art/books/gospels.webp",
  gospel: "/art/books/gospels.webp",
};

const eraArtwork: Record<number, string> = Object.fromEntries(
  [1950, 1960, 1970, 1980, 1990, 2000, 2010, 2020].map((decade) => [
    decade,
    `/art/eras/${decade}s.webp`,
  ]),
);
const fallbackArtwork = [
  ...topicArtwork.map((item) => item.image),
  ...Object.values(bibleBookArtwork).filter(
    (image, index, values) => values.indexOf(image) === index,
  ),
  ...Object.values(eraArtwork),
];

function stableBucket(value: string) {
  let number = 0;
  for (const character of value)
    number = ((number << 5) - number + character.charCodeAt(0)) | 0;
  return Math.abs(number);
}

function decadeFor(year?: number | null) {
  return year ? Math.floor(year / 10) * 10 : null;
}

function unique(items: string[]) {
  return [...new Set(items.filter(Boolean))];
}

export function artworkCandidates(
  work: Pick<
    CanonicalWork,
    | "id"
    | "title"
    | "topics"
    | "contributors"
    | "artworkUrl"
    | "scriptureRefs"
    | "year"
  >,
  scope = "",
) {
  const text =
    `${work.title} ${work.topics.join(" ")} ${work.scriptureRefs.map((item) => item.book).join(" ")}`.toLowerCase();
  const candidates = [
    work.artworkUrl && !work.artworkUrl.startsWith("/artwork/")
      ? work.artworkUrl
      : "",
  ];
  candidates.push(
    ...work.contributors.map(
      (person) => contributorArtwork[person.slug]?.image || "",
    ),
  );
  for (const [book, image] of Object.entries(bibleBookArtwork))
    if (text.includes(book)) candidates.push(image);
  for (const topic of topicArtwork)
    if (topic.words.some((word) => text.includes(word)))
      candidates.push(topic.image);
  const era = decadeFor(work.year);
  if (era && eraArtwork[era]) candidates.push(eraArtwork[era]);
  const offset = stableBucket(`${scope}:${work.id}`) % fallbackArtwork.length;
  candidates.push(
    ...fallbackArtwork.slice(offset),
    ...fallbackArtwork.slice(0, offset),
  );
  return unique(candidates);
}

export function contextualArtwork(
  work: Pick<
    CanonicalWork,
    | "id"
    | "title"
    | "topics"
    | "contributors"
    | "artworkUrl"
    | "scriptureRefs"
    | "year"
  >,
) {
  return artworkCandidates(work)[0];
}

/** Assign a visual to every item in one screen before rendering it, avoiding repeats until the pool is exhausted. */
export function assignArtwork<
  T extends Pick<
    CanonicalWork,
    | "id"
    | "title"
    | "topics"
    | "contributors"
    | "artworkUrl"
    | "scriptureRefs"
    | "year"
  >,
>(works: T[], scope: string) {
  const used = new Set<string>();
  return works.map((work) => {
    const candidates = artworkCandidates(work, scope);
    const selected =
      candidates.find((image) => !used.has(image)) ||
      candidates[stableBucket(`${scope}:${work.id}`) % candidates.length];
    used.add(selected);
    return selected;
  });
}

export function browseArtwork(value: string) {
  const key = value.toLowerCase();
  const book = key.replace(/^book-/, "");
  if (bibleBookArtwork[book]) return bibleBookArtwork[book];
  const decade = Number(key.match(/(?:era-)?(19\d{2}|20\d{2})/)?.[1]);
  if (eraArtwork[decade]) return eraArtwork[decade];
  if (contributorArtwork[key]) return contributorArtwork[key].image;
  return fallbackArtwork[stableBucket(value) % fallbackArtwork.length];
}
